/**
 * Blocker impact selectors — the pure, browser-side rollup that exposes
 * structured-blocker delivery impact so Goal Health (batch 1) and any forecast
 * view can CONSUME it without knowing how blockers are stored.
 *
 * This is the deliberate integration seam the batch calls for: rather than
 * reaching into the blockers store, a consumer (Goal Health, a forecast panel,
 * the Team Today dashboard) takes the workspace's {@link BlockerSummary}[] and
 * asks these selectors "what is the open-blocker delivery impact on goal X?" or
 * "on workstream Y?". The shape below is stable so it can be folded into a
 * future health computation additively.
 *
 * Only OPEN blockers count toward delivery impact — a resolved blocker no
 * longer threatens delivery. `forecastImpactDays` is summed across the open
 * blockers that declare it; blockers without a forecast still contribute to the
 * open-blocker count (so an unquantified blocker is never invisible).
 *
 * Deliberately NOT a productivity/velocity/ranking metric: it aggregates
 * delivery RISK by the work it blocks (goal / workstream), never by person.
 *
 * Pure TS — no React.
 */

import { ageDays, isOpen } from './blockerModel';
import type { BlockerSummary } from './types';

/** Aggregated open-blocker delivery impact scoped to a single entity. */
export interface BlockerImpact {
  /** Number of OPEN blockers in scope. */
  openBlockerCount: number;
  /** Sum of forecastImpactDays across in-scope open blockers that declare one. */
  totalForecastImpactDays: number;
  /** Largest single forecastImpactDays among in-scope open blockers (0 if none). */
  maxForecastImpactDays: number;
  /** Count of in-scope open blockers with NO forecastImpactDays (unquantified). */
  unquantifiedBlockers: number;
  /** Longest current blocking age (days) among in-scope open blockers (0 if none). */
  oldestBlockingDays: number;
  /** Whether any in-scope open blocker depends on an EXTERNAL owner. */
  hasExternalDependency: boolean;
  /** The in-scope open blockers themselves (for drill-down rendering). */
  blockers: BlockerSummary[];
}

/** An empty impact (no open blockers in scope). */
export function emptyBlockerImpact(): BlockerImpact {
  return {
    openBlockerCount: 0,
    totalForecastImpactDays: 0,
    maxForecastImpactDays: 0,
    unquantifiedBlockers: 0,
    oldestBlockingDays: 0,
    hasExternalDependency: false,
    blockers: [],
  };
}

/** Folds a set of (already open, already in-scope) blockers into an impact. */
function foldImpact(scoped: BlockerSummary[]): BlockerImpact {
  const impact = emptyBlockerImpact();
  impact.blockers = scoped;
  impact.openBlockerCount = scoped.length;
  for (const b of scoped) {
    const days = b.forecastImpactDays;
    if (days !== undefined && Number.isFinite(days) && days > 0) {
      impact.totalForecastImpactDays += days;
      if (days > impact.maxForecastImpactDays) impact.maxForecastImpactDays = days;
    } else {
      impact.unquantifiedBlockers += 1;
    }
    const age = ageDays(b);
    if (age > impact.oldestBlockingDays) impact.oldestBlockingDays = age;
    if (b.internalExternal === 'EXTERNAL') impact.hasExternalDependency = true;
  }
  return impact;
}

/** All OPEN blockers from a set. */
export function selectOpenBlockers(blockers: BlockerSummary[]): BlockerSummary[] {
  return blockers.filter(isOpen);
}

/**
 * Open-blocker delivery impact for a single goal (blockers whose
 * `affectedGoalId` matches). Returns an empty impact when none apply.
 */
export function selectBlockerImpactForGoal(
  blockers: BlockerSummary[],
  goalId: string,
): BlockerImpact {
  if (!goalId) return emptyBlockerImpact();
  const scoped = blockers.filter((b) => isOpen(b) && b.affectedGoalId === goalId);
  return foldImpact(scoped);
}

/**
 * Open-blocker delivery impact for a single workstream (blockers whose
 * `blockedWorkstreamId` matches). Returns an empty impact when none apply.
 */
export function selectBlockerImpactForWorkstream(
  blockers: BlockerSummary[],
  workstreamId: string,
): BlockerImpact {
  if (!workstreamId) return emptyBlockerImpact();
  const scoped = blockers.filter(
    (b) => isOpen(b) && b.blockedWorkstreamId === workstreamId,
  );
  return foldImpact(scoped);
}

/**
 * Builds a goalId → {@link BlockerImpact} map across all OPEN blockers that
 * declare an `affectedGoalId`. A consumer (Goal Health, a forecast view) can
 * look up a goal's delivery impact in O(1). Goals with no open blockers are
 * simply absent from the map (use {@link emptyBlockerImpact} as the default).
 */
export function selectBlockerImpactByGoal(
  blockers: BlockerSummary[],
): Map<string, BlockerImpact> {
  const byGoal = new Map<string, BlockerSummary[]>();
  for (const b of blockers) {
    if (!isOpen(b) || !b.affectedGoalId) continue;
    const list = byGoal.get(b.affectedGoalId) ?? [];
    list.push(b);
    byGoal.set(b.affectedGoalId, list);
  }
  const out = new Map<string, BlockerImpact>();
  for (const [goalId, scoped] of byGoal) out.set(goalId, foldImpact(scoped));
  return out;
}

/** Overall open-blocker delivery impact across the whole workspace. */
export function selectOverallBlockerImpact(
  blockers: BlockerSummary[],
): BlockerImpact {
  return foldImpact(selectOpenBlockers(blockers));
}

/**
 * A single human-readable delivery-impact line for a goal/workstream, suitable
 * for a health reason or a forecast caption. Empty string when there is no
 * open-blocker impact.
 */
export function describeBlockerImpact(impact: BlockerImpact): string {
  if (impact.openBlockerCount === 0) return '';
  const parts: string[] = [];
  parts.push(
    `${impact.openBlockerCount} open ${impact.openBlockerCount === 1 ? 'blocker' : 'blockers'}`,
  );
  if (impact.totalForecastImpactDays > 0) {
    parts.push(
      `~${impact.totalForecastImpactDays} ${impact.totalForecastImpactDays === 1 ? 'day' : 'days'} forecast delivery impact`,
    );
  }
  if (impact.unquantifiedBlockers > 0) {
    parts.push(
      `${impact.unquantifiedBlockers} unquantified`,
    );
  }
  if (impact.hasExternalDependency) parts.push('external dependency');
  return parts.join(' · ');
}
